'use client';

import type { ReactNode } from 'react';
import React from 'react';
import { mergeProps } from 'react-aria';
import { useSharedHoverContext } from 'components/SharedHover';
import { VisuallyHidden } from 'components/VisuallyHidden';
import { focusStyles, hoverTransition } from 'mixins/interactive';
import styled, { css } from 'styled-components';
import { whenHovered } from 'utils';
import isValidProp from '@emotion/is-prop-valid';
import { theme } from '@frameio/vapor-theme';
import type { ButtonProps, ButtonVariant } from './Button';
import { buttonBoxSizes, buttonReset, buttonVariants } from './Button';
import { ghostVariant } from './ghost';
import type { ButtonColor, ButtonSize } from './types';

/**
 * @deprecated `IconButtonProps` has been deprecated along with `IconButton`.
 * Use `IconButton2` instead.
 */
export type IconButtonProps = {
  icon: ReactNode;
  label: string;
  as?: any;
} & Omit<ButtonProps, 'iconBefore' | 'iconAfter' | 'block'>;

/**
 * @deprecated Avoid using this constant as this module is deprecated.
 */
export const baseIconButtonStyles = css<{ $size: ButtonSize }>`
  ${buttonReset};
  align-items: center;
  border-radius: ${theme.radii[6]};
  display: inline-flex;
  flex-shrink: 0;
  height: ${(p) => `${theme.spaces[buttonBoxSizes[p.$size]]}`};
  justify-content: center;
  outline: none;
  padding: 0;
  position: relative;
  vertical-align: middle;
  width: ${(p) => `${theme.spaces[buttonBoxSizes[p.$size]]}`};
  ${hoverTransition};
  ${focusStyles};
`;

/**
 * @deprecated Avoid using this constant as this module is deprecated.
 */
export const iconButtonGhostVariant = css<
  { $color?: 'secondary' | 'neutral' } & any
>`
  ${ghostVariant()};

  ${(p) => {
    if (p.$color === 'secondary') {
      return css`
        color: ${theme.colors.icon.secondary};
        ${whenHovered} {
          color: ${theme.colors.icon.interactive.hover};
        }
      `;
    }

    return css``;
  }}
`;

const StyledButton = styled.button.withConfig({
  shouldForwardProp: (prop) => isValidProp(prop),
})<{
  $variant: ButtonVariant;
  $size: ButtonSize;
  $color: ButtonColor;
  $sharedHoverEnabled: boolean;
  disabled: boolean;
}>`
  ${baseIconButtonStyles};
  ${(p) => buttonVariants[p.$variant]};
  ${(p) =>
    p.$variant === 'ghost' &&
    (p.$color === 'secondary' || p.$color === 'neutral') &&
    iconButtonGhostVariant};
  ${(p) =>
    p.$sharedHoverEnabled &&
    `
      will-change: transform;
    `}
`;

/**
 * @deprecated use `IconButton2` instead. This component will be removed
 * in a future release.
 */
export const IconButton = React.forwardRef(
  (
    {
      as,
      color = 'secondary',
      icon,
      label,
      size = 'medium',
      type: typeProp,
      variant = 'contained',
      disabled = false,
      onClick,
      ...other
    }: IconButtonProps,
    ref: any
  ) => {
    const isLink = Boolean(other.href);
    const iconChild = icon as any;

    // Only apply "type='button'" when the dom element is actually a button.
    const type =
      isLink || typeProp || (as && as !== 'button') ? typeProp : 'button';

    const { onEnterTarget, onLeaveTarget, enabled } = useSharedHoverContext();

    function onMouseEnter(e: React.MouseEvent) {
      onEnterTarget(e.currentTarget.getBoundingClientRect());
    }

    function onMouseLeave() {
      onLeaveTarget();
    }

    return (
      <StyledButton
        ref={ref}
        $color={color}
        $sharedHoverEnabled={enabled && variant === 'ghost'}
        $size={size}
        $variant={variant}
        as={isLink ? 'a' : as}
        disabled={disabled}
        onClick={onClick as React.MouseEventHandler<HTMLButtonElement>}
        type={type}
        {...mergeProps(
          other,
          // @ts-expect-error FIXME(strictNullChecks)
          variant === 'ghost' && {
            onMouseEnter,
            onMouseLeave,
          }
        )}
      >
        <VisuallyHidden>{label}</VisuallyHidden>
        {React.cloneElement(iconChild as any, {
          'aria-hidden': true,
          fill: 'none',
          ...(iconChild.props || {}),
        })}
      </StyledButton>
    );
  }
);
